import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { landClaimBlockReason, claimWithFreshCopy, shouldReturnToLobby } from '@/game/landClaimTap';
import { phaseLabel, phaseScreen } from '@/game/phases';
// SSE imported via sseManager in the code below
import { sseManager } from '@/services/sse';
import { selectUser } from '@/store/authSlice';
import { selectCurrentGame, fetchGameState, updateGameState, clearGame, submitTurn } from '@/store/gameSlice';
import { selectAnimationPhase, setAnimationPhase, setShowLandingPad, setInteractionEnabled } from '@/store/uiSlice';
import { apiClient } from '@/services/api';
import { ensureMusic, playCountdown, playSound, stopMusic } from '@/audio/rushAudio';
import { useDebugEnabled } from '@/debug/debugPreference';
import { useAvatarWalking } from '@/hooks/useAvatarWalking';
import { hexEqual, normalizeHexCoord } from '@/utils/hexPathfinding';
import type { AppDispatch } from '@/store';
import type { WebSocketMessage, SSEMessage, Plot } from '@/types';
import HexGrid from '@/components/HexGrid/HexGrid';
import StorePanel from '@/components/StorePanel/StorePanel';
import PlayerPanel from '@/components/PlayerPanel/PlayerPanel';
import GameStatusPanel from '@/components/GameStatusPanel/GameStatusPanel';
import { MoonImage3D } from '@/components/MoonImage/MoonImage3D';
import { MoonDetailModal } from '@/components/MoonDetailModal/MoonDetailModal';
import StartGameAnimation from '@/components/StartGameAnimation/StartGameAnimation';
import DebugPanel from '@/components/DebugPanel/DebugPanel';
import { RoverDebugOverlay } from '@/components/RoverDebugOverlay/RoverDebugOverlay';
import RoverPlacementModal from '@/components/RoverPlacementModal/RoverPlacementModal';
import StoreModal from '@/components/Store/StoreModal';
import StoreMap, { type RoverData } from '@/components/Store/StoreMap';
import DevelopmentPhaseOverlay from '@/components/DevelopmentPhaseOverlay/DevelopmentPhaseOverlay';
import GrowReport from '@/components/GrowReport/GrowReport';
import { MuteButton } from '@/components/MuteButton/MuteButton';
import { QuitGameButton } from '@/components/QuitGame/QuitGameButton';
import { StoreLandOffer } from '@/components/QuitGame/StoreLandOffer';
import type { RoverType } from '@/components/RoverPlacementModal/RoverPlacementModal';
import './Game.css';

const TERRAIN_LABELS: Record<string, string> = {
  lunar: 'Lunar Mesa',
  highland: 'Highlands',
  crater: 'Crater Field',
  mountain: 'Mountain Range',
  regolith: 'Regolith Dunes',
  base: 'Base',
  valley: 'Valley',
};

function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const game = useSelector(selectCurrentGame);
  const debugOn = useDebugEnabled();
  const animationPhase = useSelector(selectAnimationPhase);
  const surveyOrClaim = game?.phase === 'start_game' || game?.phase === 'land_claim';
  const landingFocus = Boolean(game?.phase === 'start_game');
  
  // Ref to track latest game state for use in async callbacks (avoids stale closure issues)
  const gameRef = useRef(game);
  useEffect(() => {
    gameRef.current = game;
  }, [game]);

  // Debug: Log game state when it changes
  useEffect(() => {
    if (game?.players) {
      console.log('🎮 Redux game state updated. Players FULL:', JSON.stringify(game.players.map(p => ({
        userId: p.userId,
        position: p.position
      })), null, 2));
    }
    // Debug: Log land claim timer state
    if (game?.phase === 'land_claim') {
      console.log('🗺️ Land claim state:', JSON.stringify({
        phase: game.phase,
        hasTimer: !!game.landClaimTimer,
        timerActive: game.landClaimTimer?.active,
        currentPlotId: game.landClaimTimer?.currentPlotId,
        currentIndex: game.landClaimTimer?.currentIndex,
        isLastTile: game.landClaimTimer?.isLastTile
      }));
    }
  }, [game]);

  const winnerName = (() => {
    if (game?.phase !== 'ended' || !game.players?.length) return '';
    const prices = game.market?.prices || {};
    const score = (player: (typeof game.players)[number]) => {
      const resources = player.resources || {};
      const goods = (['energy', 'minerals', 'food', 'oxygen', 'hydrogen', 'water', 'helium3'] as const)
        .reduce((sum, key) => sum + (Number(resources[key]) || 0) * (Number(prices[key]) || 0), 0);
      return (player.money || 0) + goods;
    };
    const leader = [...game.players].sort((a, b) => score(b) - score(a))[0];
    return leader ? (leader.playerName || leader.username || 'A colonist') : '';
  })();

  const heardGrant = useRef<string | null>(null);

  useEffect(() => () => stopMusic(), []);

  useEffect(() => {
    const plotId = game?.landClaimTimer?.currentPlotId;
    if (game?.phase !== 'land_claim' || !plotId || plotId === heardGrant.current) return;
    heardGrant.current = plotId;
    playSound('grant');
  }, [game?.phase, game?.landClaimTimer?.currentPlotId]);

  useEffect(() => {
    if (game?.phase !== 'development') return;
    playCountdown(Math.ceil(game.developmentTimer?.timeRemaining || 0));
  }, [game?.phase, game?.developmentTimer?.timeRemaining, game?.developmentTimer?.currentPlayerId]);

  useEffect(() => {
    if (game?.phase === 'auction' && gameId) {
      navigate(`/auction/${gameId}`);
    }
  }, [game?.phase, gameId, navigate]);
  
  // Separate ref for development phase polling (to not interfere with main polling)
  const devPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stoppedMainPollRef = useRef(false);
  
  // During development phase: Slow poll during human's turn (for timer), fast poll during AI turns
  useEffect(() => {
    if (game?.phase !== 'development') {
      if (devPollIntervalRef.current) {
        clearInterval(devPollIntervalRef.current);
        devPollIntervalRef.current = null;
      }
      // Development clears the main poll. Start it again or the client stays on
      // production while the server is already in the auction, and the auction never closes.
      if (stoppedMainPollRef.current && gameId && !sseManager.isConnected()) {
        stoppedMainPollRef.current = false;
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(() => {
          dispatch(fetchGameState(gameId));
        }, 1000);
      }
      return;
    }
    
    const timer = game.developmentTimer;
    const myPlayer = game.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
    const isMyTurn = timer?.currentPlayerId === myPlayer?.playerId;
    
    // Clear any existing development interval to set up the right one
    if (devPollIntervalRef.current) {
      clearInterval(devPollIntervalRef.current);
      devPollIntervalRef.current = null;
    }
    
    // Also stop main polling during development to avoid conflicts
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      stoppedMainPollRef.current = true;
    }
    
    if (isMyTurn && timer?.active && gameId) {
      // Human's turn - slow poll (2s) to keep timer updated without disrupting local movement
      console.log('⏱️ Development phase (YOUR TURN) - slow polling for timer updates');
      devPollIntervalRef.current = setInterval(() => {
        dispatch(fetchGameState(gameId));
      }, 2000); // Poll every 2 seconds for timer updates
    } else if (timer?.active && gameId) {
      // AI's turn - fast poll to see AI actions on the map
      console.log('🔄 Development phase (AI TURN) - fast polling to watch AI');
      devPollIntervalRef.current = setInterval(() => {
        dispatch(fetchGameState(gameId));
      }, 500); // Poll every 500ms to see AI movement updates
    }
    
    return () => {
      // Only cleanup development polling ref
      if (devPollIntervalRef.current) {
        clearInterval(devPollIntervalRef.current);
        devPollIntervalRef.current = null;
      }
    };
  }, [game?.phase, game?.developmentTimer?.currentPlayerId, game?.developmentTimer?.active, gameId, user, dispatch]);
  
  // Where this player is standing during development. Polls return the last
  // server coordinate, which can be a plot from earlier in the turn, so the
  // map keeps this spot until the server confirms it.
  const [clientSpot, setClientSpot] = useState<{
    playerId: string;
    at: { q: number; r: number; s: number };
    mule: { q: number; r: number; s: number };
  } | null>(null);
  const clientSpotRef = useRef(clientSpot);
  clientSpotRef.current = clientSpot;

  // Walking animation state
  const currentPlayer = game?.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
  const spotForMe = clientSpot && currentPlayer && clientSpot.playerId === (currentPlayer.playerId || currentPlayer.userId)
    ? clientSpot
    : null;
  const {
    isWalking,
    avatarPosition,
    roverPosition,
    walkTo,
  } = useAvatarWalking({
    initialAvatarPosition: spotForMe?.at || currentPlayer?.position || { q: 0, r: 0, s: 0 },
    initialRoverPosition: spotForMe?.mule || (currentPlayer?.position ? { 
      q: currentPlayer.position.q + 1, 
      r: currentPlayer.position.r, 
      s: currentPlayer.position.s - 1 
    } : { q: 1, r: 0, s: -1 }),
    stepDuration: 300, // 300ms per step
    onWalkComplete: (finalPosition) => {
      console.log('✅ Walk complete! Final position:', finalPosition);
      
      // Mark that onWalkComplete has fired - prevent useEffect from overwriting
      walkCompleteHandledRef.current = true;
      const meId = currentPlayer?.playerId || currentPlayer?.userId || user?.userId;
      if (meId) {
        const spot = { playerId: meId, at: finalPosition, mule: finalPosition };
        clientSpotRef.current = spot;
        setClientSpot(spot);
        if (gameId && game?.phase === 'development') {
          apiClient.completeDevelopmentTurn({ gameId, stand: finalPosition }).then((updated) => {
            const latest = clientSpotRef.current;
            if (!latest || !hexEqual(latest.at, finalPosition)) return;
            dispatch(updateGameState({
              ...updated,
              players: updated.players?.map((p) => (
                p.playerId === meId || p.userId === meId
                  ? { ...p, position: latest.at, _roverPosition: latest.mule }
                  : p
              )),
            }));
          }).catch((error) => {
            console.error('Failed to save map position', error);
          });
        }
      }
      
      // Check if player arrived at base hex (center) during development phase
      const isAtBase = finalPosition.q === 0 && finalPosition.r === 0 && finalPosition.s === 0;
      if (isAtBase && game?.phase === 'development') {
        // Calculate entry direction based on previous position
        const prevPos = prevAvatarPosRef.current;
        let entryDir: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
        
        if (prevPos) {
          // Determine direction based on where player came from
          // In hex coordinates: positive r is generally "down", negative r is "up"
          // positive q is "right", negative q is "left"
          if (prevPos.r > 0) {
            entryDir = 'bottom'; // Came from below
          } else if (prevPos.r < 0) {
            entryDir = 'top'; // Came from above
          } else if (prevPos.q > 0) {
            entryDir = 'right'; // Came from right
          } else if (prevPos.q < 0) {
            entryDir = 'left'; // Came from left
          }
        }
        
        console.log('🏪 Arrived at base from direction:', entryDir, 'Opening store...');
        setStoreEntryDirection(entryDir);
        setRoverLostOnMap(false);
        setRoverDeploymentMode(false);
        setShowStoreMap(true);
      }
      
      // Give rover time to catch up with physics animation
      // Set rover target to avatar position, it will smoothly glide there
      if (game && user) {
        console.log('🤖 BEFORE update - Current game.players:', JSON.stringify(game.players.map(p => ({
          userId: p.userId,
          position: p.position,
          _roverPosition: (p as any)._roverPosition
        })), null, 2));
        
        const updatedPlayers = game.players?.map(p => {
          if (p.userId === user.userId || p.playerId === user.userId) {
            console.log(`🤖 Updating player ${p.userId}: position=${JSON.stringify(finalPosition)}, _roverPosition=${JSON.stringify(finalPosition)}`);
            return {
              ...p,
              position: finalPosition,
              _roverPosition: finalPosition // Set target, rover will smoothly catch up
            };
          }
          return p;
        });
        
        console.log('🤖 AFTER update - Updated players:', JSON.stringify(updatedPlayers.map(p => ({
          userId: p.userId,
          position: p.position,
          _roverPosition: (p as any)._roverPosition
        })), null, 2));
        
        dispatch(updateGameState({
          ...game,
          players: updatedPlayers
        }));
      }
    }
  });
  
  // Sync walking animation positions to Redux for rendering
  // Use a ref to prevent infinite loops by comparing previous values
  const prevAvatarPosRef = useRef<typeof avatarPosition | null>(null);
  const wasWalkingRef = useRef(false);
  const walkCompleteHandledRef = useRef(false); // Track if onWalkComplete has fired
  
  useEffect(() => {
    const turnId = game?.phase === 'development' ? game?.developmentTimer?.currentPlayerId : null;
    setClientSpot((prev) => {
      if (!prev) return prev;
      if (!turnId || prev.playerId !== turnId) return null;
      return prev;
    });
  }, [game?.phase, game?.developmentTimer?.currentPlayerId]);

  useEffect(() => {
    // A new walk starts from the door. Clear the "walk already finished" latch first,
    // otherwise the latch blocks the reset and the avatar keeps the previous hex.
    if (isWalking && !wasWalkingRef.current) {
      console.log('🚶 Walk started - resetting walkCompleteHandledRef');
      wasWalkingRef.current = true;
      walkCompleteHandledRef.current = false;
    }

    // If onWalkComplete has already fired, NEVER sync again until next walk
    if (walkCompleteHandledRef.current) {
      console.log('🛑 onWalkComplete already handled - skipping ALL syncs until next walk');
      return;
    }
    
    // Only sync rover position WHILE walking is in progress
    // Do NOT sync when walking stops - let onWalkComplete set final position
    if (isWalking && game && user) {
      
      // Check if rover has reached avatar (walk completion detected in hook)
      const roverReachedAvatar = (
        roverPosition.q === avatarPosition.q &&
        roverPosition.r === avatarPosition.r &&
        roverPosition.s === avatarPosition.s
      );
      
      if (roverReachedAvatar) {
        console.log('🛑 Rover reached avatar in hook state - skipping sync, letting onWalkComplete handle final position');
        return; // Don't sync - onWalkComplete will set final position
      }
      
      // Additional safeguard: Check if Redux already has rover at avatar position
      // (This means onWalkComplete already ran, so don't overwrite with trailing position)
      const currentPlayerInRedux = game.players?.find(p => p.userId === user.userId || p.playerId === user.userId);
      if (currentPlayerInRedux && (currentPlayerInRedux as any)._roverPosition) {
        const reduxRoverPos = (currentPlayerInRedux as any)._roverPosition;
        const reduxAvatarPos = currentPlayerInRedux.position;
        
        if (reduxRoverPos.q === reduxAvatarPos.q && 
            reduxRoverPos.r === reduxAvatarPos.r && 
            reduxRoverPos.s === reduxAvatarPos.s) {
          console.log('🛑 Redux already has rover at avatar position - onWalkComplete has set final position, skipping sync');
          return; // Don't overwrite the final converged position
        }
      }
      
      // Only update if position actually changed
      const prevPos = prevAvatarPosRef.current;
      if (!prevPos || prevPos.q !== avatarPosition.q || prevPos.r !== avatarPosition.r || prevPos.s !== avatarPosition.s) {
        console.log('🔄 Syncing walking position to Redux:', avatarPosition, 'Rover target:', roverPosition);
        prevAvatarPosRef.current = avatarPosition;
        const walkingId = currentPlayer?.playerId || currentPlayer?.userId || user.userId;
        if (walkingId) {
          const spot = { playerId: walkingId, at: avatarPosition, mule: roverPosition };
          clientSpotRef.current = spot;
          setClientSpot(spot);
        }

        const updatedPlayers = game.players?.map(p => {
          if (p.userId === user.userId || p.playerId === user.userId) {
            return {
              ...p,
              position: avatarPosition,
              // Store rover position for rendering (trailing behind while walking)
              _roverPosition: roverPosition
            };
          }
          return p;
        });

        dispatch(updateGameState({
          ...game,
          players: updatedPlayers
        }));
      }
    } else if (!isWalking && wasWalkingRef.current) {
      // Walk just stopped - onWalkComplete will handle final position
      console.log('🛑 Walk stopped - skipping sync to preserve final rover position from onWalkComplete');
      wasWalkingRef.current = false;
    }
  }, [isWalking, avatarPosition, roverPosition, game, user, dispatch, currentPlayer?.playerId, currentPlayer?.userId]);
  useEffect(() => {
    if (game?.phase === 'ended') {
      stopMusic();
      playSound('win');
      return;
    }
    if (game?.phase) ensureMusic();
  }, [game?.phase]);
  const [showMoonModal, setShowMoonModal] = useState(false);
  const [isClaimingLand, setIsClaimingLand] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  
  // Rover placement modal state
  const [showRoverPlacementModal, setShowRoverPlacementModal] = useState(false);
  const [selectedPlotForRover, setSelectedPlotForRover] = useState<Plot | null>(null);
  
  // Store modal state
  const [showStoreModal, setShowStoreModal] = useState(false);
  
  // StoreMap state (for Development Phase)
  const [showStoreMap, setShowStoreMap] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle('store-open', showStoreMap);
    return () => document.body.classList.remove('store-open');
  }, [showStoreMap]);

  useEffect(() => {
    const developing = game?.phase === 'development';
    document.body.classList.toggle('development-focus', developing);
    document.body.classList.toggle('debug-open', developing && debugOpen);
    return () => {
      document.body.classList.remove('development-focus');
      document.body.classList.remove('debug-open');
    };
  }, [game?.phase, debugOpen]);
  const [storeEntryDirection, setStoreEntryDirection] = useState<'top' | 'bottom' | 'left' | 'right' | null>(null);
  
  // Rover deployment state (after exiting store with outfitted rover)
  const [roverDeploymentMode, setRoverDeploymentMode] = useState(false);
  const [pendingRoverData, setPendingRoverData] = useState<RoverData | null>(null);
  
  // Rover runaway animation state
  const [roverRunawayAnimation, setRoverRunawayAnimation] = useState<{
    active: boolean;
    startPosition: { q: number; r: number; s: number } | null;
    direction: 'up' | 'down' | 'left' | 'right' | null;
  }>({ active: false, startPosition: null, direction: null });
  
  // Rover deployment animation state (rover transforming into building)
  const [roverDeployAnimation, setRoverDeployAnimation] = useState<{
    active: boolean;
    plotId: string | null;
    outfitType: string | null;
    phase: 'spinning' | 'transforming' | 'complete';
  }>({ active: false, plotId: null, outfitType: null, phase: 'spinning' });
  
  // Track if player just lost their rover - prevents auto-reopening store
  const [roverLostOnMap, setRoverLostOnMap] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4500);
    return () => window.clearTimeout(id);
  }, [notice]);
  useEffect(() => {
    if (game?.bulletin) setNotice(game.bulletin);
  }, [game?.bulletin]);
  const storeOpenedForRef = useRef('');
  const hadMuleRef = useRef(false);
  const wasMyDevTurnRef = useRef(false);
  
  const prevPhaseRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Client-side land claim highlighting state
  const [currentHighlightedPlotId, setCurrentHighlightedPlotId] = useState<string | null>(null);
  const [grantCountdown, setGrantCountdown] = useState<number | null>(null);
  const [currentTileIndex, setCurrentTileIndex] = useState<number>(0);
  const tileHighlightStartTimeRef = useRef<number>(0);  // When current tile started being highlighted (client time)

  useEffect(() => {
    if (!gameId) return;

    // Fetch initial game state
    dispatch(fetchGameState(gameId)).then((result) => {
      if (fetchGameState.rejected.match(result) && shouldReturnToLobby(String(result.payload || ''))) {
        console.error('❌ Game not found, redirecting to lobby');
        navigate('/lobby', { replace: true });
      }
    });

    if (!user?.userId) return;

    // ============================================================================
    // HYBRID REAL-TIME STRATEGY: SSE + Polling
    // ============================================================================
    // 1. Try SSE first (instant updates, cost-effective)
    // 2. Fall back to polling (1s interval to match land_claim tile duration)
    // 3. WebSocket support removed (LocalStack incompatible, too expensive)
    // ============================================================================

    let sseUnsubscribe: (() => void) | null = null;
    let sseConnected = false;

    // Start SSE connection
    sseManager.connect(gameId, user.userId)
      .then(() => {
        console.log('✅ SSE connected, stopping polling fallback');
        sseConnected = true;
        
        // Stop polling when SSE is connected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      })
      .catch((error) => {
        console.warn('⚠️  SSE connection failed, using polling fallback:', error);
        sseConnected = false;
      });

    // Listen for SSE messages
    sseUnsubscribe = sseManager.onMessage((message: SSEMessage) => {
      console.log('📨 SSE message received:', message.type);
      
      if (message.type === 'GAME_STATE_UPDATE' && message.gameState) {
        dispatch(updateGameState(message.gameState));
      } else if (message.type === 'PHASE_CHANGE' && message.gameState) {
        console.log(`🎉 Phase changed via SSE: ${message.oldPhase} → ${message.newPhase}`);
        dispatch(updateGameState(message.gameState));
      }
    });

    // Start polling as fallback (will be stopped if SSE connects)
    const startPolling = () => {
      console.log('🔄 Starting polling fallback (1-second interval for land_claim)');
      
      pollIntervalRef.current = setInterval(() => {
        // SKIP polling during development phase to allow local movement testing
        const currentGame = (window as any).store?.getState()?.game?.currentGame;
        if (currentGame?.phase === 'development') {
          console.log('⏸️  Skipping poll - development phase (local movement only)');
          return;
        }
        
        // Only poll if SSE is not connected
        if (!sseManager.isConnected()) {
          console.log('📊 Polling for game state update...');
          dispatch(fetchGameState(gameId)).then((result: any) => {
            if (result.payload?.landClaimTimer) {
              console.log('📡 Polled landClaimTimer:', JSON.stringify({
                active: result.payload.landClaimTimer.active,
                currentPlotId: result.payload.landClaimTimer.currentPlotId,
                currentIndex: result.payload.landClaimTimer.currentIndex
              }));
            } else {
              console.log('📡 Polled but no landClaimTimer in response');
            }
          });
        }
      }, 1000); // Match tile duration (1 second)
    };

    // Give SSE 2 seconds to connect before starting polling
    const pollingStartTimer = setTimeout(() => {
      if (!sseConnected) {
        startPolling();
      }
    }, 2000);

    // Cleanup
    return () => {
      if (pollingStartTimer) {
        clearTimeout(pollingStartTimer);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (sseUnsubscribe) {
        sseUnsubscribe();
      }
      sseManager.disconnect();
    };
  }, [gameId, user?.userId, dispatch, navigate]);

  useEffect(() => {
    if (!gameId || !game?.phase) return;
    const screen = phaseScreen(game.phase);
    if (screen === 'avatars' || screen === 'lobby' || game.phase === 'name_selection') {
      navigate(`/lobby/avatars/${gameId}`, { replace: true });
    }
  }, [gameId, game?.phase, navigate]);

  // Initialize animation state when entering start_game phase (but don't override during animation)
  useEffect(() => {
    const previousPhase = prevPhaseRef.current;
    
    // Only initialize when first entering start_game phase
    if (game?.phase === 'start_game' && previousPhase !== 'start_game') {
      console.log('🎬 Entering start_game phase, initializing animation');
      dispatch(setAnimationPhase('moon_approach')); // Start animation
      dispatch(setShowLandingPad(false));
      dispatch(setInteractionEnabled(false));
    } else if (game?.phase && game.phase !== 'start_game' && animationPhase !== 'idle') {
      // Reset animation when leaving start_game phase
      console.log(`🎬 Leaving start_game phase (now ${game.phase}), resetting animation`);
      dispatch(setAnimationPhase('idle'));
      dispatch(setShowLandingPad(false));
      dispatch(setInteractionEnabled(true));
    }
    
    prevPhaseRef.current = game?.phase ?? null;
  }, [game?.phase, animationPhase, dispatch]);

  useEffect(() => {
    document.body.classList.toggle('landing-focus', landingFocus);
    return () => document.body.classList.remove('landing-focus');
  }, [landingFocus]);

  const introUntil = game?.phase === 'land_claim' ? game.landClaimTimer?.introUntil : undefined;
  useEffect(() => {
    if (!introUntil) {
      setGrantCountdown(null);
      return;
    }
    const tick = () => {
      const left = Date.parse(introUntil) - Date.now();
      setGrantCountdown(left > 0 ? Math.ceil(left / 1000) : null);
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [introUntil]);

  // Server-driven land claim highlighting - use currentPlotId directly from server
  // Server updates this every second via land_claim_timer Lambda
  useEffect(() => {
    console.log('🔄 Land claim useEffect running:', { phase: game?.phase, hasTimer: !!game?.landClaimTimer });
    
    if (!game || game.phase !== 'land_claim') {
      setCurrentHighlightedPlotId(null);
      setCurrentTileIndex(0);
      return;
    }

    const landClaimTimer = game.landClaimTimer;
    console.log('⏱️ Land claim timer:', { active: landClaimTimer?.active, currentPlotId: landClaimTimer?.currentPlotId });
    
    if (!landClaimTimer?.active) {
      console.log('⚠️ Land claim timer not active, clearing highlight');
      setCurrentHighlightedPlotId(null);
      return;
    }

    // Use server-provided currentPlotId directly
    const serverPlotId = landClaimTimer.currentPlotId;
    console.log('🎯 Setting highlighted plot to:', serverPlotId);
    setCurrentHighlightedPlotId(serverPlotId || null);
    
    // Record when this tile started being highlighted (for reaction time calculation)
    if (serverPlotId && serverPlotId !== currentHighlightedPlotId) {
      tileHighlightStartTimeRef.current = Date.now();
      
      // Calculate tile index from tileSequence if available
      const tileSequence = landClaimTimer.tileSequence || [];
      const index = tileSequence.indexOf(serverPlotId);
      if (index >= 0) {
        setCurrentTileIndex(index);
      }
      
      console.log(`🎯 Highlighting tile: ${serverPlotId} (index: ${index >= 0 ? index : '?'}/${tileSequence.length})`);
    }
  }, [game, game?.landClaimTimer?.currentPlotId, game?.landClaimTimer?.active]);

  // Development Phase: Auto-show StoreMap for current player's turn
  // Shows store for human player's turn AND for AI players when they're in the store
  useEffect(() => {
    if (!game || !user || game.phase !== 'development') {
      // Close store if we leave development phase
      if (showStoreMap) {
        setShowStoreMap(false);
      }
      return;
    }

    const timer = game.developmentTimer;
    if (!timer || !timer.active) {
      console.log('⚠️  Development timer not active');
      return;
    }

    // Find current user's player object
    const myPlayer = game.players?.find(p => p.userId === user.userId || p.playerId === user.userId);
    
    if (!myPlayer) {
      console.log('⚠️  Current player not found');
      return;
    }

    // Check if it's this player's turn using the timer's currentPlayerId
    const isMyTurn = timer.currentPlayerId === myPlayer.playerId;
    
    // AI store phases where we should show the store
    const AI_STORE_PHASES = ['entering_store', 'buying_rover', 'outfitting', 'exiting_store', 'visiting_lounge'];
    const aiInStore = timer.currentPlayerIsAI && AI_STORE_PHASES.includes(timer.aiAction || '');
    
    // If it's an AI player's turn and they're in the store, show store for spectating
    if (timer.currentPlayerIsAI) {
      if (aiInStore && !showStoreMap) {
        console.log(`🤖 AI player in store (${timer.aiAction}), opening store for spectating...`);
        setStoreEntryDirection('bottom');
        setShowStoreMap(true);
      } else if (!aiInStore && showStoreMap) {
        console.log('🤖 AI player left store, closing store');
        setShowStoreMap(false);
      }
      return;
    }
    
    const undeployed = (myPlayer.purchasedRovers || []).some((rover) => !rover.deployed) || Boolean(pendingRoverData?.hasRover);
    if (undeployed) hadMuleRef.current = true;

    // Open the store once when this player's clock starts. Leaving it does not end the turn.
    const turnKey = `${timer.currentPlayerId}|${timer.turnStartTime}`;
    if (isMyTurn && showStoreMap) storeOpenedForRef.current = turnKey;
    const carrying = roverDeploymentMode || Boolean(pendingRoverData?.hasRover);
    if (isMyTurn && !showStoreMap && !roverLostOnMap && !carrying && storeOpenedForRef.current !== turnKey) {
      storeOpenedForRef.current = turnKey;
      setStoreEntryDirection(null);
      setShowStoreMap(true);
    } else if (!isMyTurn && showStoreMap) {
      setShowStoreMap(false);
    }
  }, [game, game?.phase, game?.developmentTimer, game?.developmentTimer?.currentPlayerId, game?.developmentTimer?.aiAction, user, showStoreMap, roverLostOnMap, pendingRoverData, roverDeploymentMode]);

  useEffect(() => {
    if (!game || !user || game.phase !== 'development') {
      wasMyDevTurnRef.current = false;
      return;
    }
    const meNow = game.players?.find((player) => player.userId === user.userId || player.playerId === user.userId);
    const mine = game.developmentTimer?.currentPlayerId === meNow?.playerId;
    if (wasMyDevTurnRef.current && !mine && hadMuleRef.current) {
      setRoverRunawayAnimation({
        active: true,
        startPosition: meNow?.position || { q: 0, r: 0, s: 0 },
        direction: 'up',
      });
      setShowStoreMap(false);
      setRoverDeploymentMode(false);
      setPendingRoverData(null);
      hadMuleRef.current = false;
      window.setTimeout(() => {
        setRoverRunawayAnimation({ active: false, startPosition: null, direction: null });
      }, 1500);
    }
    wasMyDevTurnRef.current = Boolean(mine);
  }, [game, game?.phase, game?.developmentTimer?.currentPlayerId, user]);

  /**
   * Handle double-click on hex to place rover
   */
  const handleRoverPlacement = (plot: Plot) => {
    console.log('🖱️🖱️ Double-click handler called for plot:', plot);
    
    if (!game || game.phase !== 'development') {
      console.log('⚠️ Not in development phase');
      return;
    }
    
    if (!currentPlayer) {
      console.log('⚠️ No current player');
      return;
    }
    
    // Get coordinates - support both 'coordinates' and 'hexCoords' field names
    const plotCoords = plot.coordinates || plot.hexCoords;
    
    // Check if player is on this hex
    const playerPos = currentPlayer.position;
    if (!playerPos || !plotCoords) {
      console.log('⚠️ Player has no position or plot has no coordinates');
      return;
    }
    
    const isOnPlot = 
      playerPos.q === plotCoords.q &&
      playerPos.r === plotCoords.r &&
      playerPos.s === plotCoords.s;
    
    if (!isOnPlot) {
      console.log('⚠️ Player is not on this plot. Player pos:', playerPos, 'Plot coords:', plotCoords);
      return;
    }
    
    // Check if player has available rovers
    const availableRovers = (currentPlayer.rovers || 0) - (currentPlayer.deployedRovers?.length || 0);
    if (availableRovers <= 0) {
      console.log('⚠️ No available rovers');
      setClaimError('No rovers available! Visit the store to purchase more.');
      setTimeout(() => setClaimError(null), 3000);
      return;
    }
    
    console.log('✅ Opening rover placement modal for plot:', plot);
    setSelectedPlotForRover(plot);
    setShowRoverPlacementModal(true);
  };

  /**
   * Handle rover type selection and placement
   */
  /**
   * Handle store interactions (purchase/outfit/sell)
   */
  const handlePurchaseRover = async () => {
    if (!gameId || !user?.userId) return;
    
    console.log('🤖 Purchasing rover...');
    
    try {
      const data = await apiClient.purchaseRover(gameId);
      console.log('✅ Rover purchased:', data);
      
      // Refresh game state
      if (gameId) {
        dispatch(fetchGameState(gameId) as any);
      }
    } catch (error: any) {
      console.error('❌ Error purchasing rover:', error);
      alert(`Failed to purchase rover: ${error.message}`);
      throw error;
    }
  };
  
  const handleOutfitRover = async (roverType: RoverType) => {
    if (!gameId || !user?.userId) return;
    
    console.log('🔧 Outfitting rover:', roverType);
    
    try {
      const data = await apiClient.outfitRover(gameId, roverType.id);
      console.log('✅ Rover outfitted:', data);
      
      // Refresh game state
      if (gameId) {
        dispatch(fetchGameState(gameId) as any);
      }
    } catch (error: any) {
      console.error('❌ Error outfitting rover:', error);
      alert(`Failed to outfit rover: ${error.message}`);
      throw error;
    }
  };
  
  const handleSellResources = async (resources: Record<string, number>) => {
    if (!gameId || !user?.userId) return;
    
    console.log('💰 Selling resources:', resources);
    
    try {
      const data = await apiClient.sellResources(gameId, resources);
      console.log('✅ Resources sold:', data);
      
      // Refresh game state
      if (gameId) {
        dispatch(fetchGameState(gameId) as any);
      }
    } catch (error: any) {
      console.error('❌ Error selling resources:', error);
      alert(`Failed to sell resources: ${error.message}`);
      throw error;
    }
  };

  /**
   * Get the adjacent hex position based on exit direction
   * Returns the hex coordinate adjacent to the base (0,0,0) in the given direction
   */
  const getExitHexPosition = (exitDirection: 'top' | 'bottom' | 'left' | 'right'): { q: number; r: number; s: number } => {
    // Hex neighbors based on cube coordinates (q, r, s where q + r + s = 0)
    // For a pointy-top hex grid:
    switch (exitDirection) {
      case 'top':
        // Northwest direction (above center in pointy-top)
        return { q: 0, r: -1, s: 1 };
      case 'bottom':
        // Southeast direction (below center in pointy-top)
        return { q: 0, r: 1, s: -1 };
      case 'left':
        // West direction
        return { q: -1, r: 0, s: 1 };
      case 'right':
        // East direction
        return { q: 1, r: 0, s: -1 };
      default:
        // Default to southeast (below)
        return { q: 0, r: 1, s: -1 };
    }
  };

  /**
   * Handle exiting store with an outfitted rover - enter deployment mode
   */
  const handleExitWithRover = (roverData: RoverData) => {
    console.log('🚀 Exiting store with rover for deployment:', roverData);
    
    // Calculate the exit position based on exit direction
    const exitDirection = roverData.exitDirection || 'bottom';
    const exitPosition = getExitHexPosition(exitDirection);
    console.log('📍 Setting avatar position to adjacent hex:', exitPosition, 'based on exit:', exitDirection);
    rememberDoorSpot(exitPosition);
    
    // Update Redux state with new position (this will sync to useAvatarWalking hook)
    // Also set _roverPosition so the rover renders following the avatar
    // Increment rovers count so HexGrid knows to render the rover
    if (game?.players && user?.userId) {
      const updatedPlayers = game.players.map(p => {
        if (p.userId === user.userId || p.playerId === user.userId) {
          const currentRovers = p.rovers || 0;
          const currentDeployed = p.deployedRovers?.length || 0;
          // Only increment rovers if player doesn't already have an undeployed rover
          const newRoverCount = currentRovers > currentDeployed ? currentRovers : currentRovers + 1;
          
          console.log('🤖 Updating player rover state:', {
            currentRovers,
            currentDeployed,
            newRoverCount,
            position: exitPosition
          });
          
          return {
            ...p,
            position: exitPosition,
            // Set rover position to same as avatar - it will trail behind when walking
            _roverPosition: exitPosition,
            // Increment rovers count so HexGrid renders the rover
            rovers: newRoverCount,
            // Update money after purchase
            money: roverData.money,
          };
        }
        return p;
      });
      
      dispatch(updateGameState({
        ...game,
        players: updatedPlayers
      }));
    }
    
    setShowStoreMap(false);
    setPendingRoverData(roverData);
    setRoverDeploymentMode(Boolean(roverData.outfitType));
    hadMuleRef.current = true;
    if (!roverData.outfitType) {
      setNotice('This mule has no equipment. Take it back to the store and outfit it before time runs out.');
    }
  };
  
  /**
   * Handle deploying rover to a plot
   */
  const handleDeployRoverToPlot = async (plotId: string) => {
    if (!pendingRoverData || !gameId || !user?.userId || !game) return;
    
    console.log('🤖 Deploying rover to plot:', plotId, 'with outfit:', pendingRoverData.outfitType);
    
    const outfitType = pendingRoverData.outfitType; // Save before clearing
    
    try {
      
      // TODO: Call deploy_rover Lambda
      console.log('📡 TODO: Call deploy_rover Lambda with:', {
        gameId,
        plotId,
        outfitType,
      });
      
      // Start deployment animation
      setRoverDeployAnimation({
        active: true,
        plotId,
        outfitType,
        phase: 'spinning',
      });
      
      // Clear rover from following avatar immediately
      const updatedPlayersBeforeAnimation = game.players?.map(p => {
        if (p.userId === user.userId || p.playerId === user.userId) {
          return {
            ...p,
            _roverPosition: undefined, // Stop rover from rendering while animating
          };
        }
        return p;
      });
      
      dispatch(updateGameState({
        ...game,
        players: updatedPlayersBeforeAnimation,
      }));
      
      // Animation sequence: spinning (500ms) -> transforming (800ms) -> complete
      setTimeout(() => {
        setRoverDeployAnimation(prev => ({ ...prev, phase: 'transforming' }));
      }, 500);
      
      setTimeout(() => {
        setRoverDeployAnimation(prev => ({ ...prev, phase: 'complete' }));
        
        // Use ref to get latest game state (avoids stale closure)
        const latestGame = gameRef.current;
        if (!latestGame) return;
        
        // Update game state with deployed rover
        const updatedPlayers = latestGame.players?.map(p => {
          if (p.userId === user.userId || p.playerId === user.userId) {
            const currentDeployed = p.deployedRovers || [];
            return {
              ...p,
              deployedRovers: [...currentDeployed, { plotId, outfitType }],
              _roverPosition: undefined,
            };
          }
          return p;
        });
        
        // Update plots to show the deployed rover
        const updatedPlots = latestGame.map?.plots?.map(plot => {
          // Compare both plotId (string) and id (number, converted to string)
          const plotIdStr = plot.plotId || String(plot.id);
          if (plotIdStr === plotId) {
            console.log('🏗️ Adding rover to plot:', plotIdStr, 'type:', outfitType);
            return {
              ...plot,
              rover: { type: outfitType, deployedAt: new Date().toISOString() },
            };
          }
          return plot;
        });
        
        dispatch(updateGameState({
          ...latestGame,
          players: updatedPlayers,
          map: {
            ...latestGame.map,
            plots: updatedPlots,
          },
        }));
        
        // Show success message only the first time ever
        const hasSeenDeploymentSuccess = localStorage.getItem('rush_seen_deployment_success');
        if (!hasSeenDeploymentSuccess) {
          localStorage.setItem('rush_seen_deployment_success', 'true');
        }
        
        console.log('✅ Rover deployed! Building placed on plot.');
      }, 1300);
      
      setTimeout(() => {
        setRoverDeployAnimation({ active: false, plotId: null, outfitType: null, phase: 'spinning' });
        setRoverDeploymentMode(false);
        setPendingRoverData(null);
        if (!gameId) return;
        apiClient.completeDevelopmentTurn({ gameId, plotId, outfitType, place: true })
          .then((updated) => {
            dispatch(updateGameState(updated));
            const me = updated.players?.find((player) => player.userId === user.userId || player.playerId === user.userId);
            const still = (me?.purchasedRovers || []).find((rover) => !rover.deployed);
            hadMuleRef.current = Boolean(still);
            if (still) {
              setPendingRoverData({ hasRover: true, outfitType: still.outfitType || null, money: me?.money || 0 });
              setRoverDeploymentMode(false);
              setNotice('The mule you pulled off has no equipment. Take it to the store and outfit it before time runs out.');
            }
          })
          .catch((error) => {
            console.error('Could not place the mule', error);
          });
      }, 1800);
      
    } catch (error: any) {
      console.error('❌ Error deploying rover:', error);
      alert(`Failed to deploy rover: ${error.message}`);
      setRoverDeployAnimation({ active: false, plotId: null, outfitType: null, phase: 'spinning' });
    }
  };
  
  const handlePickupMule = async (plotId: string) => {
    if (!gameId) return;
    try {
      const updated = await apiClient.completeDevelopmentTurn({ gameId, plotId, pickup: true });
      dispatch(updateGameState(updated));
      const me = updated.players?.find((player) => player.userId === user?.userId || player.playerId === user?.userId);
      const bare = (me?.purchasedRovers || []).find((rover) => !rover.deployed);
      setPendingRoverData(bare ? { hasRover: true, outfitType: bare.outfitType || null, money: me?.money || 0 } : null);
      setRoverDeploymentMode(false);
      hadMuleRef.current = true;
      setNotice('The plot is empty. This mule has no equipment. Take it to the store and outfit it before time runs out.');
    } catch (error: any) {
      const message = String(error?.response?.data?.message || error?.message || 'Could not take the mule');
      setNotice(message.replace(/^VALIDATION:\s*/i, ''));
    }
  };

  /**
   * Handle canceling rover deployment
   */
  const handleCancelDeployment = () => {
    setRoverDeploymentMode(false);
    setStoreEntryDirection('bottom');
    setShowStoreMap(true);
  };

  /**
   * Handle player completing their development turn early (exiting store)
   * @param deploymentData - Optional rover deployment data (plotId and outfitType)
   */
  const handleNovaLounge = async () => {
    if (!gameId) return;
    try {
      const updatedGame = await apiClient.completeDevelopmentTurn({ gameId, lounge: true });
      const result = (updatedGame as { loungeResult?: { won?: boolean; amount?: number } }).loungeResult;
      dispatch(updateGameState(updatedGame));
      setShowStoreMap(false);
      setRoverDeploymentMode(false);
      setPendingRoverData(null);
      if (result?.won) {
        setNotice(`You won ${result.amount} CT at the Nova Lounge. Your turn has ended.`);
      } else {
        setNotice('The Nova Lounge kept your money this time. Your turn has ended.');
      }
    } catch (error: any) {
      console.error('Nova Lounge failed', error);
      setNotice(error.message || 'The Nova Lounge did not answer. Try again.');
    }
  };

  const handleCompleteTurn = async (deploymentData?: { plotId?: string; outfitType?: string }) => {
    if (!gameId || !user?.userId) return;
    
    console.log('✅ Completing development turn...', deploymentData || '(no deployment)');
    
    try {
      const updatedGame = await apiClient.completeDevelopmentTurn({
        gameId,
        plotId: deploymentData?.plotId,
        outfitType: deploymentData?.outfitType,
      });
      console.log('✅ Turn completed. Updated game state:', updatedGame);
      
      // Update game state
      dispatch(updateGameState(updatedGame));
      
      // Close store
      setShowStoreMap(false);
      
      // Clear deployment mode
      setRoverDeploymentMode(false);
      setPendingRoverData(null);
      
      console.log('✅ Turn complete, next player\'s turn started');
    } catch (error: any) {
      console.error('❌ Error completing turn:', error);
      alert(`Failed to complete turn: ${error.message}`);
    }
  };

  /**
   * Stand the player and mule on the hex outside the door they used, and keep
   * that spot when the next poll returns an older server coordinate.
   */
  const rememberDoorSpot = (exitPosition: { q: number; r: number; s: number }) => {
    const meId = currentPlayer?.playerId || currentPlayer?.userId || user?.userId;
    if (!meId) return;
    const spot = { playerId: meId, at: exitPosition, mule: exitPosition };
    clientSpotRef.current = spot;
    setClientSpot(spot);
    walkCompleteHandledRef.current = false;
    if (!gameId || game?.phase !== 'development') return;
    apiClient.completeDevelopmentTurn({ gameId, stand: exitPosition }).then((updated) => {
      const latest = clientSpotRef.current;
      if (!latest || !hexEqual(latest.at, exitPosition)) return;
      dispatch(updateGameState({
        ...updated,
        players: updated.players?.map((p) => (
          p.playerId === meId || p.userId === meId
            ? { ...p, position: latest.at, _roverPosition: latest.mule }
            : p
        )),
      }));
    }).catch((error) => {
      console.error('Failed to save map position', error);
    });
  };

  /**
   * Handle exiting store without a rover - places player and mule on the hex outside that door
   */
  const handleStoreClose = (exitDirection?: 'top' | 'bottom' | 'left' | 'right') => {
    console.log('🚪 Closing store via exit:', exitDirection || 'default');
    
    // If exit direction provided, update player position to adjacent hex
    if (exitDirection && game?.players && user?.userId) {
      const exitPosition = getExitHexPosition(exitDirection);
      console.log('📍 Setting avatar position to adjacent hex:', exitPosition, 'based on exit:', exitDirection);
      rememberDoorSpot(exitPosition);
      
      const updatedPlayers = game.players.map(p => {
        if (p.userId === user.userId || p.playerId === user.userId) {
          return {
            ...p,
            position: exitPosition,
            _roverPosition: exitPosition,
          };
        }
        return p;
      });
      
      dispatch(updateGameState({
        ...game,
        players: updatedPlayers
      }));
    }

    setShowStoreMap(false);
  };

  /**
   * Handle rover type selection and placement
   */
  const handlePlaceRover = async (roverType: RoverType) => {
    if (!selectedPlotForRover || !gameId || !user?.userId) return;
    
    console.log('🤖 Placing rover:', roverType, 'on plot:', selectedPlotForRover);
    
    try {
      // TODO: Call place_rover Lambda
      console.log('TODO: Call place_rover Lambda with:', {
        gameId,
        userId: user.userId,
        plotId: selectedPlotForRover.id,
        roverType: roverType.id,
        cost: roverType.cost,
      });
      
      // Close modal
      setShowRoverPlacementModal(false);
      setSelectedPlotForRover(null);
      
      // TODO: Update game state after successful placement
    } catch (error) {
      console.error('Error placing rover:', error);
      setClaimError('Failed to place rover. Please try again.');
      setTimeout(() => setClaimError(null), 3000);
    }
  };

  const handleLandClaim = async (plot: Plot) => {
    if (!gameId || !game || !user?.userId || isClaimingLand) return;

    if (game.landSale?.active && plot.plotId === game.landSale.plotId) {
      setIsClaimingLand(true);
      setClaimError(null);
      const result = await dispatch(submitTurn({
        gameId,
        turnData: { action: 'buy_plot', data: { plotId: plot.plotId } },
      }));
      setIsClaimingLand(false);
      if (submitTurn.rejected.match(result)) {
        setClaimError(String(result.payload || 'Could not buy that plot'));
        setTimeout(() => setClaimError(null), 4000);
      }
      return;
    }

    const highlightedPlotId = currentHighlightedPlotId || game.landClaimTimer?.currentPlotId || null;
    const sequence = game.landClaimTimer?.tileSequence || [];
    const highlightIndex = highlightedPlotId ? sequence.indexOf(highlightedPlotId) : -1;
    const previousPlotId = highlightIndex > 0 ? sequence[highlightIndex - 1] : null;
    const block = landClaimBlockReason({
      phase: game.phase,
      highlightedPlotId,
      previousPlotId,
      tileSequence: sequence,
      recentOffers: game.landClaimTimer?.recentOffers,
      plot,
      alreadyClaimed: Boolean(game.landClaimPhase?.claimedBy?.[user.userId]),
      countdown: grantCountdown ?? game.landClaimTimer?.countdown,
    });
    if (block) {
      setClaimError(block);
      setTimeout(() => setClaimError(null), 3000);
      return;
    }

    const ownerId = plot.ownerId || plot.claimedBy?.playerId;
    if (ownerId && ownerId !== user.userId) {
      console.log('⚠️ Plot appears claimed, but sending claim anyway for reaction-time resolution');
    }

    const reactionTimeMs = tileHighlightStartTimeRef.current > 0
      ? Date.now() - tileHighlightStartTimeRef.current
      : 500;

    setIsClaimingLand(true);
    setClaimError(null);

    const send = (etag?: string) => dispatch(submitTurn({
      gameId,
      turnData: {
        action: 'claim_land',
        data: {
          plotId: plot.plotId,
          reactionTimeMs,
          tileIndex: currentTileIndex,
        },
        etag,
      },
    }));

    try {
      const result = await claimWithFreshCopy(
        send,
        async () => {
          const fresh = await dispatch(fetchGameState(gameId));
          if (fetchGameState.fulfilled.match(fresh)) return fresh.payload?.etag;
          return gameRef.current?.etag;
        },
        gameRef.current?.etag || game.etag,
        (value) => (submitTurn.rejected.match(value) ? String(value.payload || 'Failed to claim land') : null),
      );

      if (submitTurn.fulfilled.match(result)) {
        console.log(`✅ Land claimed successfully (reaction: ${reactionTimeMs}ms)`);
      } else {
        const errorMessage = (result.payload as string) || 'Failed to claim land';
        setClaimError(errorMessage);
        setTimeout(() => setClaimError(null), 5000);
      }
    } catch (error: any) {
      console.error('Error claiming land:', error);
      setClaimError(error.message || 'Failed to claim land');
      setTimeout(() => setClaimError(null), 5000);
    } finally {
      setIsClaimingLand(false);
    }
  };

  const landClaim = game?.phase === 'land_claim';
  const [landClaimFocus, setLandClaimFocus] = useState(landClaim);
  useEffect(() => {
    if (landClaim) {
      setLandClaimFocus(true);
      return;
    }
    const timer = window.setTimeout(() => setLandClaimFocus(false), 720);
    return () => window.clearTimeout(timer);
  }, [landClaim]);

  if (!game) {
    return (
      <div className="game-page">
        <div className="loading">Loading game...</div>
      </div>
    );
  }

  return (
    <div className={`game-page${landClaimFocus ? ' land-claim-focus' : ''}${landingFocus ? ' landing-focus' : ''}`}>
      {game?.phase === 'development' && debugOn && (
        <button
          type="button"
          className="debug-reveal"
          onClick={() => setDebugOpen((open) => !open)}
        >
          {debugOpen ? 'Hide debug' : 'Debug'}
        </button>
      )}
      <button
        type="button"
        className="game-lobby-button"
        onClick={() => {
          stopMusic();
          dispatch(clearGame());
          navigate('/lobby');
        }}
      >
        Lobby
      </button>
      <MuteButton />
      <QuitGameButton gameId={gameId} game={game} />
      {/* Start Game Animation Overlay */}
      {game.phase === 'start_game' && animationPhase !== 'complete' && <StartGameAnimation />}
      
      <header className="game-header">
        {game.landmark && (
          <div className="game-header-moon">
            <MoonImage3D 
              landmark={game.landmark} 
              size="small"
              onClick={() => setShowMoonModal(true)}
            />
          </div>
        )}
        <div className="game-header-title">
          <h1>{game.phase === 'ended' ? 'Game over' : (game.landmark?.displayName || 'Lunar Outpost')}</h1>
          {game.phase === 'ended' && winnerName ? (
            <span className="game-id-subtitle">{winnerName} wins</span>
          ) : null}
        </div>
      </header>

      {/* Moon Detail Modal */}
      {showMoonModal && game.landmark && (
        <MoonDetailModal 
          landmark={game.landmark}
          onClose={() => setShowMoonModal(false)}
        />
      )}

      {/* Development Phase Turn Overlay */}
      {game.phase === 'development' && (
        <DevelopmentPhaseOverlay 
          onMyTurnStart={() => {
            console.log('🎮 Your turn started!');
            // Optionally play a sound or show notification
          }}
        />
      )}

      {/* Land claim uses the full screen. The caption floats on the board. */}
      <main className="game-main">
        {gameId ? <StoreLandOffer game={game} gameId={gameId} /> : null}
        <div className="game-layout">
          {/* Left Sidebar: Game Status and Store Panel */}
          <aside className="game-sidebar game-sidebar-left">
            <GameStatusPanel />
            <StorePanel />
          </aside>

          {/* Center: Game Board */}
          <div className="game-board">
            {game.map && game.map.plots ? (
              <HexGrid
                plots={game.map?.plots || []}
                players={
                  // During development phase: Show the CURRENT TURN's player avatar (human or AI)
                  // This allows spectators to watch AI players move on the map
                  // During land_claim phase: Don't show any players (tiles highlighted sequentially)
                  // When development timer is inactive: Don't show any players (phase transitioning)
                    game.phase === 'land_claim' || game.phase === 'production'
                    ? [] 
                    : game.phase === 'development'
                      ? (() => {
                          const timer = game.developmentTimer;
                          // When timer is inactive or no current player, show no avatars
                          // This prevents rovers from appearing when phase is transitioning
                          if (!timer?.active || !timer?.currentPlayerId) {
                            return [];
                          }
                          
                          // Find the current turn's player (could be human or AI)
                          const currentTurnPlayer = game.players?.find(p => p.playerId === timer.currentPlayerId);
                          if (!currentTurnPlayer) {
                            return [];
                          }
                          
                          // Check if it's the human user's turn
                          const myPlayer = game.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
                          const isMyTurn = timer.currentPlayerId === myPlayer?.playerId;
                          
                          // For human player: use local walking state
                          // For AI player: they're always "walking" (animated by server position updates)
                          const playerIsWalking = isMyTurn ? isWalking : (timer.aiAction === 'walking_to_plot');
                          
                          // For AI players, check if they have a rover based on aiHasRover flag
                          // The rover should only show when:
                          // - AI has purchased a rover (outfitting phase and after)
                          // - AI hasn't deployed it yet (not in returning_to_store, visiting_lounge, done)
                          // Note: 'buying_rover' means walking TO the store, not purchased yet!
                          const hasRoverFollowing = timer.currentPlayerIsAI
                            ? timer.aiHasRover === true || ['outfitting', 'exiting_store', 'walking_to_plot'].includes(timer.aiAction || '')
                            : (currentTurnPlayer.rovers || 0) > (currentTurnPlayer.deployedRovers?.length || 0);
                          
                          const meId = currentTurnPlayer.playerId || currentTurnPlayer.userId;
                          const remembered = isMyTurn && clientSpot && clientSpot.playerId === meId ? clientSpot : null;
                          const shownAt = isMyTurn && isWalking
                            ? avatarPosition
                            : (remembered?.at || currentTurnPlayer.position || { q: 0, r: 0, s: 0 });
                          const shownMule = isMyTurn && isWalking
                            ? roverPosition
                            : (remembered?.mule || shownAt);
                          return [{
                            playerId: meId, 
                            playerNumber: currentTurnPlayer.playerNumber, 
                            color: currentTurnPlayer.color,
                            position: shownAt,
                            rovers: hasRoverFollowing ? 1 : 0,
                            deployedRovers: currentTurnPlayer.deployedRovers,
                            _roverPosition: shownMule,
                            _isWalking: playerIsWalking
                          }];
                        })()
                      : game.players?.map(p => ({ 
                          playerId: p.playerId || p.userId, 
                          playerNumber: p.playerNumber, 
                          color: p.color,
                          position: p.position,
                          rovers: p.rovers,
                          deployedRovers: p.deployedRovers,
                          _roverPosition: p._roverPosition,
                          _isWalking: (p.userId === user?.userId || p.playerId === user?.userId) ? isWalking : false
                        })) || []
                }
                // Always pass ALL players' colors for plot ownership borders
                playerColors={game.players?.reduce((acc, p) => {
                  const playerId = p.playerId || p.userId;
                  if (playerId && p.color) {
                    acc[playerId] = p.color;
                  }
                  return acc;
                }, {} as Record<string, string>) || {}}
                levelId={game.level || 'lunar'}
                highlightedPlotId={game.phase === 'land_claim' ? (currentHighlightedPlotId || game.landClaimTimer?.currentPlotId || null) : null}
                productionPills={game.phase === 'production' ? (() => {
                  const pills: Record<string, { resource: string; count: number }> = {};
                  const rows = (game.productionTimer?.production || []) as Array<{ details?: Array<{ plotId?: string; resource?: string; final?: number }> }>;
                  for (const row of rows) {
                    for (const detail of row.details || []) {
                      if (!detail.plotId) continue;
                      const amount = detail.final || 0;
                      pills[detail.plotId] = {
                        resource: detail.resource || 'energy',
                        count: Math.max(1, Math.min(4, Math.ceil(amount / 40) || 1)),
                      };
                    }
                  }
                  for (const plot of game.map?.plots || []) {
                    if (!plot.rover?.type) continue;
                    const key = plot.plotId || (plot.id != null ? String(plot.id) : '');
                    if (key && !pills[key]) pills[key] = { resource: plot.rover.type, count: 1 };
                  }
                  return pills;
                })() : undefined}
                onHexClick={(plot) => {
                  console.log('Hex clicked:', plot);
                  
                  // Get coordinates - support both 'coordinates' and 'hexCoords' field names
                  const plotCoords = plot?.coordinates || plot?.hexCoords;
                  
                  // Defensive check for plot coordinates
                  if (!plotCoords) {
                    console.error('❌ Plot has no coordinates:', plot);
                    return;
                  }
                  
                  // Defensive check for avatar position
                  const safeAvatarPosition = avatarPosition || { q: 0, r: 0, s: 0 };
                  
                  // Handle land claim in land_claim phase
                  if (game.phase === 'land_claim') {
                    handleLandClaim(plot);
                  } else if (game.phase === 'development') {
                    // BLOCK INTERACTION if it's not the human player's turn
                    const timer = game.developmentTimer;
                    const myPlayer = game.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
                    const isMyTurn = timer?.currentPlayerId === myPlayer?.playerId;
                    
                    if (!isMyTurn) {
                      console.log('🚫 Not your turn - watching AI player');
                      return; // Block all interaction during AI turns
                    }
                    
                    // Check if player clicked on base hex (center)
                    const isBaseHex = plotCoords.q === 0 && plotCoords.r === 0 && plotCoords.s === 0;
                    
                    // Check if avatar is already at base
                    const isAtBase = safeAvatarPosition.q === 0 && safeAvatarPosition.r === 0 && safeAvatarPosition.s === 0;
                    
                    if (isBaseHex) {
                      if (isAtBase) {
                        console.log('🏪 Opening store at base hex');
                        setStoreEntryDirection('bottom');
                        setRoverDeploymentMode(false);
                        setShowStoreMap(true);
                      } else {
                        // Walk to base first (whether in deployment mode or not)
                        console.log('🚶 Walking to base hex');
                        if (isWalking) {
                          console.log('⚠️  Already walking, ignoring click');
                          return;
                        }
                        walkTo(plotCoords);
                      }
                    } else {
                      // Development phase: Walk avatar to clicked hex
                      // This works both in normal mode and rover deployment mode
                      console.log('🚶 Starting walk to:', plotCoords);
                      
                      // Don't start a new walk if already walking
                      if (isWalking) {
                        console.log('⚠️  Already walking, ignoring click');
                        return;
                      }
                      
                      // Start walking animation
                      walkTo(plotCoords);
                    }
                  } else {
                    // Other phases: robot placement, etc. (future)
                    console.log('Plot clicked in phase:', game.phase);
                  }
                }}
                onHexDoubleClick={(plot) => {
                  console.log('Hex double-clicked:', plot);
                  
                  // Get coordinates - support both 'coordinates' and 'hexCoords' field names
                  const plotCoords = plot?.coordinates || plot?.hexCoords;
                  
                  // Defensive check for plot coordinates
                  if (!plotCoords) {
                    console.error('❌ Plot has no coordinates:', plot);
                    return;
                  }
                  
                  // Defensive check for avatar position
                  const safeAvatarPos = avatarPosition || { q: 0, r: 0, s: 0 };
                  
                  // BLOCK INTERACTION if it's not the human player's turn (development phase)
                  if (game.phase === 'development') {
                    const timer = game.developmentTimer;
                    const myPlayer = game.players?.find(p => p.userId === user?.userId || p.playerId === user?.userId);
                    const isMyTurn = timer?.currentPlayerId === myPlayer?.playerId;
                    
                    if (!isMyTurn) {
                      console.log('🚫 Not your turn - watching AI player');
                      return; // Block all interaction during AI turns
                    }

                    const ownedPlot = (plot.ownerId || plot.claimedBy?.playerId) === user?.userId
                      || (plot.ownerId || plot.claimedBy?.playerId) === currentPlayer?.playerId;
                    if (ownedPlot && plot.rover && !pendingRoverData?.outfitType) {
                      if (!hexEqual(safeAvatarPos, plotCoords)) {
                        setNotice('Walk onto your mule, then press it again to take it.');
                        return;
                      }
                      if (pendingRoverData?.hasRover) {
                        setNotice('Outfit the mule you are carrying before taking another.');
                        return;
                      }
                      handlePickupMule(plot.plotId || String(plot.id));
                      return;
                    }
                  }
                  
                  // Handle rover deployment in deployment mode
                  if (roverDeploymentMode && pendingRoverData) {
                    // Check if player is on this plot (must walk to it first)
                    // Use hexEqual to handle coordinates with or without s
                    const isOnPlot = hexEqual(safeAvatarPos, plotCoords);
                    
                    if (!isOnPlot) {
                      // Player must walk to the plot first
                      console.log('⚠️ Must walk to plot before deploying. Avatar at:', safeAvatarPos, 'Plot at:', plotCoords);
                      setClaimError('Walk to this plot first, then double-click to deploy!');
                      setTimeout(() => setClaimError(null), 3000);
                      return;
                    }
                    
                    // Check if this plot is owned by the current player
                    // Check both ownerId (debug games) and claimedBy.playerId (real games from submit_turn)
                    const plotOwnerId = plot.ownerId || plot.claimedBy?.playerId;
                    const isOwnedByPlayer = plotOwnerId === user?.userId || plotOwnerId === currentPlayer?.playerId;
                    
                    console.log('🔍 Ownership check:', {
                      plotId: plot.id,
                      plotOwnerId,
                      'plot.ownerId': plot.ownerId,
                      'plot.claimedBy': plot.claimedBy,
                      userId: user?.userId,
                      playerId: currentPlayer?.playerId,
                      isOwnedByPlayer
                    });
                    
                    if (!isOwnedByPlayer) {
                      // Rover runs away! Player loses it
                      console.log('🏃 Rover ran away! Deploying to unowned plot:', plot.id);
                      
                      // Trigger runaway animation (always plays)
                      setRoverRunawayAnimation({
                        active: true,
                        startPosition: { ...safeAvatarPos },
                        direction: ['up', 'down', 'left', 'right'][Math.floor(Math.random() * 4)] as 'up' | 'down' | 'left' | 'right',
                      });
                      
                      // Show explanatory message only the first time
                      const hasSeenRunawayMessage = localStorage.getItem('rush_seen_rover_runaway');
                      if (!hasSeenRunawayMessage) {
                        // Delay the alert slightly so animation starts first
                        setTimeout(() => {
                          alert(
                            `🏃 Oh no! Your ${pendingRoverData.outfitType?.toUpperCase()} rover ran away!\n\n` +
                            `You tried to deploy on a plot you don't own. The rover escaped into the wilderness!\n\n` +
                            `You'll need to purchase a new rover from the store.`
                          );
                          localStorage.setItem('rush_seen_rover_runaway', 'true');
                        }, 100);
                      }
                      
                      setRoverLostOnMap(true);
                      hadMuleRef.current = false;
                      if (gameId) {
                        apiClient.completeDevelopmentTurn({ gameId, runaway: true }).then((updated) => {
                          dispatch(updateGameState(updated));
                        }).catch((error) => {
                          console.error('Could not record the runaway mule', error);
                        });
                      }
                      
                      // Clear deployment mode after animation completes
                      setTimeout(() => {
                        setRoverDeploymentMode(false);
                        setPendingRoverData(null);
                        setRoverRunawayAnimation({ active: false, startPosition: null, direction: null });
                      }, 1500); // Animation duration
                      
                      // Don't teleport back to store - player must walk back
                      return;
                    }
                    
                    // A cultivated plot you own swaps: your mule stays, the old one comes off bare.
                    if (plot.rover && isOwnedByPlayer) {
                      handleDeployRoverToPlot(plot.plotId || String(plot.id));
                      return;
                    }
                    if (plot.rover) {
                      setClaimError('This plot already has a rover deployed!');
                      setTimeout(() => setClaimError(null), 3000);
                      return;
                    }
                    
                    // Deploy rover to this plot
                    handleDeployRoverToPlot(plot.plotId || String(plot.id));
                    return;
                  }
                  
                  // Handle rover placement in development phase (legacy - for when player has rovers but not in deployment mode)
                  if (game.phase === 'development') {
                    handleRoverPlacement(plot);
                  }
                }}
                currentPlayerId={user?.userId}
                width={800}
                height={600}
                hideBaseIcon={game.phase === 'start_game' && animationPhase !== 'complete'}
                focusMode={surveyOrClaim || landClaimFocus}
                fitCamera={surveyOrClaim}
              />
            ) : (
              <div className="loading">Loading game board...</div>
            )}
            {notice && (
              <div className="game-notice" role="status">{notice}</div>
            )}
            {game.phase === 'production' && (
              <GrowReport game={game} />
            )}
            {game.phase === 'land_claim' && grantCountdown ? (
              <div className="land-claim-countdown">{grantCountdown}</div>
            ) : null}
            {game.phase === 'land_claim' && (
              <div className={`land-claim-caption${claimError ? ' is-error' : ''}`} role={claimError ? 'alert' : undefined}>
                {claimError || (
                  game.landSale?.active ? (
                    `Store sale · ${game.landSale.terrain} · $${game.landSale.price} · tap to buy`
                  ) : grantCountdown ? 'Get ready' : game.landClaimTimer?.active && game.landClaimTimer?.currentPlotId ? (
                    'Tap the highlighted plot'
                  ) : (
                    'Land claim'
                  )
                )}
              </div>
            )}
            {isClaimingLand && (
              <div className="claim-loading">
                Claiming land...
              </div>
            )}
          </div>

          {/* Right Sidebar: Player Panel */}
          <aside className="game-sidebar game-sidebar-right">
            <PlayerPanel />
          </aside>
        </div>
        
        {/* Debug Panel (bottom overlay) */}
        <DebugPanel currentPhase={game?.phase} gameId={game?.gameId} />
        
        {/* Rover Debug Overlay (shows avatar/rover positions) */}
        <RoverDebugOverlay />
        
        {/* Rover Placement Modal */}
        {showRoverPlacementModal && selectedPlotForRover && currentPlayer && (
          <RoverPlacementModal
            isOpen={showRoverPlacementModal}
            plotTerrain={selectedPlotForRover.terrain || 'unknown'}
            playerMoney={currentPlayer.money || 0}
            availableRovers={(currentPlayer.rovers || 0) - (currentPlayer.deployedRovers?.length || 0)}
            onPlaceRover={handlePlaceRover}
            onCancel={() => {
              setShowRoverPlacementModal(false);
              setSelectedPlotForRover(null);
            }}
          />
        )}
        
        {/* Store Modal */}
        {showStoreModal && currentPlayer && game && (
          <StoreModal
            isOpen={showStoreModal}
            playerMoney={currentPlayer.money || 0}
            playerResources={currentPlayer.resources || {}}
            storeInventory={{
              availableRovers: game.store?.availableRovers || 4,
              metalsInStore: game.store?.metalsInStore || 0,
              energyInStore: game.store?.energyInStore || 0,
              roversNextTurn: Math.min(
                Math.floor((game.store?.metalsInStore || 0) / 50),
                Math.floor((game.store?.energyInStore || 0) / 25),
                4
              ),
            }}
            onClose={() => setShowStoreModal(false)}
            onPurchaseRover={handlePurchaseRover}
            onOutfitRover={handleOutfitRover}
            onSellResources={handleSellResources}
          />
        )}
        
        {/* StoreMap (Development Phase - Turn-based) */}
        {showStoreMap && game?.developmentTimer && gameId && (() => {
          const timer = game.developmentTimer;
          const isAITurn = timer.currentPlayerIsAI === true;
          // Use AI player's data when it's AI turn, otherwise use current human player
          const storePlayerNumber = isAITurn 
            ? timer.currentPlayerNumber || 1 
            : currentPlayer?.playerNumber || 1;
          const storePlayerMoney = isAITurn
            ? game.players?.find(p => p.playerId === timer.currentPlayerId)?.money || 0
            : currentPlayer?.money || 0;
          const storePlayerRovers = isAITurn
            ? game.players?.find(p => p.playerId === timer.currentPlayerId)?.rovers || 0
            : currentPlayer?.rovers || 0;
          
          return (
            <StoreMap
              isOpen={showStoreMap}
              gameId={gameId}
              playerMoney={storePlayerMoney}
              playerRovers={storePlayerRovers}
              currentPlayerNumber={storePlayerNumber}
              timeRemaining={timer.timeRemaining || 0}
              turnDuration={timer.turnDuration || 30}
              entryDirection={storeEntryDirection}
              onClose={handleStoreClose}
              onNovaLounge={isAITurn ? undefined : handleNovaLounge}
              onExitWithRover={handleExitWithRover}
              mulesInStore={game.store?.availableRovers ?? 4}
              carriedMule={
                pendingRoverData?.hasRover
                  ? { outfitType: pendingRoverData.outfitType }
                  : (() => {
                      const bought = (currentPlayer?.purchasedRovers || []).find((rover) => !rover.deployed);
                      return bought ? { outfitType: bought.outfitType || null } : null;
                    })()
              }
              onEnterRoom={(roomType) => {
                console.log('🏪 Entering room:', roomType);
              }}
              // Attached rover (from rover replacement)
              attachedRover={currentPlayer?.attachedRover || null}
              onRoverSold={() => {
                if (gameId) dispatch(fetchGameState(gameId));
              }}
              onCarriedChange={(mule) => {
                if (!mule) {
                  setPendingRoverData(null);
                  setRoverDeploymentMode(false);
                  hadMuleRef.current = false;
                  return;
                }
                setPendingRoverData({ hasRover: true, outfitType: mule.outfitType, money: currentPlayer?.money || 0 });
                setRoverDeploymentMode(false);
                hadMuleRef.current = true;
              }}
              // AI spectating props
              isAITurn={isAITurn}
              aiPlayerName={timer.currentPlayerName || 'AI Player'}
              aiAction={timer.aiAction || ''}
              aiOutfitType={timer.aiOutfitType || ''}
              aiRoverCycle={timer.aiActionData?.roverCycle || 1}
            />
          );
        })()}
        
        {/* Rover Deployment Mode Overlay */}
        {roverDeploymentMode && pendingRoverData && !roverRunawayAnimation.active && (
          <div className="rover-deployment-overlay">
            <div className="rover-deployment-banner">
              <h2>🚀 Deploy Your Rover</h2>
              <p>Outfit: <strong>{pendingRoverData.outfitType?.toUpperCase()}</strong></p>
              <p>The same timer keeps running. Tap to walk. Double-tap your empty plot to deploy.</p>
              <p>Double-tap a plot that already has your mule to swap. The mule you pull off comes away bare.</p>
              <p>You can go back to the store and cultivate another plot if time is left.</p>
              <p className="warning-text">If the timer hits zero, an undeployed mule runs away and the money is gone.</p>
              <button 
                className="cancel-deployment-btn"
                onClick={handleCancelDeployment}
              >
                Return to store
              </button>
            </div>
          </div>
        )}
        
        {/* Rover Runaway Animation Overlay */}
        {roverRunawayAnimation.active && (
          <div className="rover-runaway-overlay">
            <div className={`rover-runaway-sprite runaway-${roverRunawayAnimation.direction}`}>
              🤖💨
            </div>
            <div className="rover-runaway-text">
              Your rover ran away!
            </div>
          </div>
        )}
        
        {/* Rover Deployment Animation Overlay */}
        {roverDeployAnimation.active && (
          <div className="rover-deploy-animation-overlay">
            <div className={`rover-deploy-animation rover-deploy-${roverDeployAnimation.phase}`}>
              {roverDeployAnimation.phase === 'spinning' && (
                <div className="deploy-spinner">
                  <span className="deploy-rover-icon">🤖</span>
                  <span className="deploy-text">Deploying...</span>
                </div>
              )}
              {roverDeployAnimation.phase === 'transforming' && (
                <div className="deploy-transform">
                  <span className="transform-icon">✨</span>
                  <span className="deploy-text">Transforming into {roverDeployAnimation.outfitType?.toUpperCase()} harvester...</span>
                </div>
              )}
              {roverDeployAnimation.phase === 'complete' && (
                <div className="deploy-complete">
                  <span className="complete-icon">
                    {roverDeployAnimation.outfitType === 'energy' && '☀️'}
                    {roverDeployAnimation.outfitType === 'food' && '🌱'}
                    {roverDeployAnimation.outfitType === 'mining' && '⛏️'}
                    {roverDeployAnimation.outfitType === 'water' && '💧'}
                    {roverDeployAnimation.outfitType === 'helium3' && '⚛️'}
                  </span>
                  <span className="deploy-text">Deployed!</span>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default GamePage;

